/**
 * @file Switch.tsx
 * @brief Toggle switch primitive built on `@radix-ui/react-switch`.
 *
 * @details
 * Mirrors the mobile app's iOS-style toggle:
 *   - Pill-shaped track that changes background when "on" (uses the
 *     primary token).
 *   - Circular thumb that slides between the two ends.
 * - Fully keyboard-accessible (space / enter) by virtue of Radix.
 * - Designed to pair with `<Label>` via `htmlFor` / `id` — Radix
 *   handles the click target & ARIA role internally.
 *
 * @dependencies
 * - @radix-ui/react-switch
 * - shared/theme/cn (className helper)
 *
 * @references
 * - WEBPANEL/src/features/billing/components/CostAlertCard.tsx
 *   (first consumer — enabling daily cost-alert emails)
 *
 * @author DES
 * @date 26-05-2026
 */

import * as SwitchPrimitive from "@radix-ui/react-switch";
import { forwardRef } from "react";
import { cn } from "@/shared/theme/cn";

export type SwitchProps = React.ComponentPropsWithoutRef<
  typeof SwitchPrimitive.Root
>;

export const Switch = forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  SwitchProps
>(({ className, ...props }, ref) => (
  <SwitchPrimitive.Root
    ref={ref}
    className={cn(
      // Track
      "peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50",
      "data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
      className,
    )}
    {...props}
  >
    <SwitchPrimitive.Thumb
      className={cn(
        "pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform",
        "data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0",
      )}
    />
  </SwitchPrimitive.Root>
));

Switch.displayName = "Switch";
